import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Lock, ArrowUpCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { motion, AnimatePresence } from 'motion/react';
import { usePermissions } from '../../hooks/usePermissions';
import { useNavItems, type NavItem } from '../../hooks/useNavItems';

/** Destinations that get their own tab; everything else lives behind "More". */
const PRIMARY_PATHS = ['/dashboard', '/pulse', '/chat', '/calendar'];

interface NavTabProps {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: number;
  locked?: boolean;
  onLockedClick?: () => void;
}

const NavTab: React.FC<NavTabProps> = ({ to, icon: Icon, label, badge, locked, onLockedClick }) => {
  const location = useLocation();
  
  const isActive = location.pathname === to;

  const tabClasses = cn(
    "flex flex-col items-center justify-center gap-1 flex-1 h-full border-r-2 border-black last:border-r-0 hover:bg-[#FFD600] hover:text-black transition-colors dark:border-white dark:hover:bg-[#FFD600] dark:hover:text-black",
    isActive ? "bg-black text-white hover:bg-black hover:text-white dark:bg-white dark:text-black" : "text-black dark:text-white bg-white dark:bg-black",
    locked && "opacity-60"
  );

  const inner = (
    <>
      <div className="relative">
        <Icon size={20} className={isActive ? 'stroke-[3px]' : 'stroke-2'} />
        {locked ? (
          <Lock size={12} className="absolute -top-1.5 -right-1.5" />
        ) : (
          typeof badge === 'number' && badge > 0 && (
            <Badge 
              className="absolute -top-1.5 -right-1.5 px-1 min-w-[14px] h-[14px] text-[8px] justify-center border-2 border-black rounded-none bg-[#FFD600] text-black font-bold font-mono"
            >
              {badge}
            </Badge>
          )
        )}
      </div>
      <span className="text-[10px] font-bold uppercase tracking-widest font-mono hidden sm:block">{label}</span>
    </>
  );

  // Locked tabs mirror the desktop Sidebar: explain the upgrade instead of navigating.
  if (locked) {
    return (
      <button onClick={onLockedClick} title={`${label} — upgrade required`} className={tabClasses}>
        {inner}
      </button>
    );
  }

  return (
    <Link to={to} className={tabClasses}>
      {inner}
    </Link>
  );
};

export const BottomNav = () => {
  const [isMoreOpen, setIsMoreOpen] = React.useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const { role } = usePermissions();
  const { groups } = useNavItems();
  const navigate = useNavigate();

  // Flatten to a single, deduped list so the tabs and the "More" sheet
  // always describe the same set of destinations.
  const allItems = React.useMemo(() => {
    const seen = new Set<string>();
    const out: NavItem[] = [];
    groups.forEach(group => {
      group.items.forEach(item => {
        if (seen.has(item.to)) return;
        seen.add(item.to);
        out.push(item);
      });
    });
    return out;
  }, [groups]);

  const byPath = (path: string) => allItems.find(item => item.to === path);

  const home = byPath('/dashboard');
  const pulse = byPath('/pulse');
  const chat = byPath('/chat');
  const calendar = byPath('/calendar');

  const moreItems = allItems.filter(item => !PRIMARY_PATHS.includes(item.to));

  const handleMoreItemClick = (item: NavItem) => {
    if (item.locked) {
      setIsMoreOpen(false);
      setShowUpgradeModal(true);
      return;
    }
    setIsMoreOpen(false);
    navigate(item.to);
  };

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t-4 border-black flex items-center justify-around z-50 pb-safe shadow-[0_-8px_0_rgba(0,0,0,1)] dark:bg-black dark:border-white dark:shadow-[0_-8px_0_rgba(255,255,255,1)]">
        {home && <NavTab to={home.to} icon={home.icon} label="Home" badge={home.badge} />}
        {pulse && <NavTab to={pulse.to} icon={pulse.icon} label={pulse.label} badge={pulse.badge} />}
        {chat && (
          <NavTab
            to={chat.to}
            icon={chat.icon}
            label={chat.label}
            badge={chat.badge}
            locked={chat.locked}
            onLockedClick={() => setShowUpgradeModal(true)}
          />
        )}
        {calendar && <NavTab to={calendar.to} icon={calendar.icon} label={calendar.label} badge={calendar.badge} />}
        <button 
          onClick={() => setIsMoreOpen(true)}
          className="flex flex-col items-center justify-center gap-1 flex-1 h-full text-black hover:bg-[#FFD600] transition-colors dark:text-white dark:hover:bg-[#FFD600] dark:hover:text-black"
        >
          <Menu size={20} className="stroke-2" />
          <span className="text-[10px] font-bold uppercase tracking-widest font-mono hidden sm:block">More</span>
        </button>
      </nav>

      <AnimatePresence>
        {isMoreOpen && (
          <div className="fixed inset-0 z-[100] md:hidden">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMoreOpen(false)}
              className="absolute inset-0 bg-navy-900/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="absolute inset-x-0 bottom-0 bg-white border-t-8 border-black shadow-[0_-8px_0_rgba(0,0,0,1)] flex flex-col max-h-[80vh] rounded-none dark:bg-black dark:border-white dark:shadow-[0_-8px_0_rgba(255,255,255,1)]"
            >
              <div className="flex items-center justify-between p-4 border-b-4 border-black dark:border-white">
                <h3 className="font-black font-display uppercase tracking-widest text-black dark:text-white text-xl">More Options</h3>
                <button onClick={() => setIsMoreOpen(false)} className="p-2 border-2 border-black bg-white text-black hover:bg-[#FFD600] transition-colors rounded-none dark:border-white dark:bg-black dark:text-white dark:hover:bg-[#FFD600] dark:hover:text-black">
                  <X size={24} className="stroke-[3px]" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto scrollbar-hide p-4 grid grid-cols-3 gap-4">
                {moreItems.length === 0 ? (
                  <p className="col-span-3 text-center text-xs font-mono uppercase tracking-widest text-gray-500 py-8">
                    Nothing else to show here.
                  </p>
                ) : (
                  moreItems.map(item => (
                    <button
                      key={item.to}
                      onClick={() => handleMoreItemClick(item)}
                      className={cn(
                        'relative flex flex-col items-center gap-3 p-4 border-2 border-black bg-white hover:bg-[#FFD600] hover:text-black hover:shadow-[4px_4px_0px_#000000] transition-all rounded-none dark:bg-black dark:border-white dark:hover:bg-[#FFD600] dark:hover:text-black dark:hover:shadow-[4px_4px_0px_#FFFFFF]',
                        item.locked && 'opacity-60'
                      )}
                    >
                      <div className="text-black dark:text-white">
                        <item.icon size={32} className="stroke-[2.5px]" />
                      </div>
                      <span className="text-[10px] font-bold font-mono uppercase tracking-widest text-black text-center dark:text-white">{item.label}</span>
                      {item.locked && (
                        <Lock size={14} className="absolute top-2 right-2 text-black dark:text-white" />
                      )}
                      {typeof item.badge === 'number' && item.badge > 0 && !item.locked && (
                        <Badge className="absolute top-2 right-2 px-1 min-w-[16px] h-[16px] text-[9px] justify-center border-2 border-black rounded-none bg-[#FFD600] text-black font-bold font-mono">
                          {item.badge}
                        </Badge>
                      )}
                    </button>
                  ))
                )}
              </div>

              <div className="p-4 border-t-4 border-black bg-white flex items-center justify-center dark:bg-black dark:border-white">
                <div className="flex items-center gap-3 px-4 py-2 bg-black border-2 border-black text-white rounded-none shadow-[4px_4px_0px_#000000] dark:bg-white dark:text-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF]">
                  <div className="w-3 h-3 bg-[#0044FF] border-2 border-white rounded-none" />
                  <span className="text-xs font-black uppercase tracking-widest font-mono">
                    {role}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Tier Upgrade Modal — mirrors the Sidebar lock behaviour on mobile. */}
      <Modal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade Your Plan"
      >
        <div className="text-center">
          <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-none flex items-center justify-center mx-auto mb-4 dark:bg-amber-500/10">
            <ArrowUpCircle size={28} />
          </div>
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
            This feature requires a higher-tier plan than your organization's current subscription.
          </p>
          <div className="flex flex-col gap-2">
            <Button variant="secondary" onClick={() => setShowUpgradeModal(false)} className="w-full">
              Maybe Later
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
